import { createRouter, createWebHistory } from 'vue-router'
import { useUiStore } from '@/stores/ui.store.ts'
import type { StartPage } from '@/utils'

export const RouteName = {
  Overview: 'overview',
  Release: 'release',
  Volume: 'volume',
  Collection: 'collection',
  Lists: 'lists',
  ListCreate: 'listCreate',
  ListEdit: 'listEdit',
  ListDetail: 'listDetail',
  Exploration: 'exploration',
  Scan: 'scan',
  Settings: 'settings',
  Profile: 'profile',
  Statistics: 'statistics',
  Test: 'test',
  OfflineSync: 'offlineSync',
  Import: 'import',
  NotificationSettings: 'notificationSettings',
  Notifications: 'notifications',
  Auth: 'auth',
  AuthIndex: 'auth.index',
  AuthCallback: 'auth.callback',
  AuthMobileCallback: 'auth.mobile-callback',
  AuthLogin: 'auth.login',
  AuthRegister: 'auth.register',
  AuthVerifyEmail: 'auth.verify-email',
  AuthForgotPassword: 'auth.forgot-password',
  AuthResetPassword: 'auth.reset-password',
} as const

export type RouteNameValue = (typeof RouteName)[keyof typeof RouteName]

export const START_PAGE_ROUTE_NAME: Record<StartPage, RouteNameValue> = {
  '': RouteName.Overview,
  lists: RouteName.Lists,
  exploration: RouteName.Exploration,
  settings: RouteName.Settings,
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: RouteName.Overview,
      component: () => import('../views/Overview.vue'),
    },
    {
      path: '/release/:releaseId',
      name: RouteName.Release,
      component: () => import('../views/Release.vue'),
    },
    {
      path: '/release/:releaseId/volume/:volumeNumber',
      name: RouteName.Volume,
      component: () => import('../views/Volume.vue'),
    },
    {
      path: '/release/:releaseId/collection/:collectionNumber',
      name: RouteName.Collection,
      component: () => import('../views/Collection.vue'),
    },
    {
      path: '/lists',
      name: RouteName.Lists,
      component: () => import('../views/Lists.vue'),
    },
    {
      path: '/lists/create',
      name: RouteName.ListCreate,
      component: () => import('../views/lists/CreateList.vue'),
    },
    {
      path: '/lists/:listId/edit',
      name: RouteName.ListEdit,
      component: () => import('../views/lists/EditList.vue'),
    },
    {
      path: '/lists/:listId',
      name: RouteName.ListDetail,
      component: () => import('../views/lists/ListDetail.vue'),
    },
    {
      path: '/exploration',
      name: RouteName.Exploration,
      component: () => import('../views/Exploration.vue'),
    },
    {
      path: '/scan',
      name: RouteName.Scan,
      component: () => import('../views/Scan.vue'),
    },
    {
      path: '/settings',
      name: RouteName.Settings,
      component: () => import('../views/Settings.vue'),
    },
    {
      path: '/profile',
      name: RouteName.Profile,
      component: () => import('../views/Profile.vue'),
    },
    {
      path: '/statistics',
      name: RouteName.Statistics,
      component: () => import('../views/Statistics.vue'),
    },
    {
      path: '/test',
      name: RouteName.Test,
      component: () => import('../views/Test.vue'),
    },
    {
      path: '/offline-sync',
      name: RouteName.OfflineSync,
      component: () => import('../views/OfflineSync.vue'),
    },
    {
      path: '/import',
      name: RouteName.Import,
      component: () => import('../views/Import.vue'),
    },
    {
      path: '/notification-settings',
      name: RouteName.NotificationSettings,
      component: () => import('../views/NotificationSettings.vue'),
    },
    {
      path: '/notifications',
      name: RouteName.Notifications,
      component: () => import('../views/Notifications.vue'),
    },
    {
      path: '/auth',
      name: RouteName.Auth,
      component: () => import('../views/auth/Index.vue'),
      children: [
        {
          path: '',
          name: RouteName.AuthIndex,
          component: () => import('../views/auth/Auth.vue'),
        },
        {
          path: 'callback/:provider',
          name: RouteName.AuthCallback,
          component: () => import('../views/auth/SSO.vue'),
        },
        {
          path: 'mobile-callback/:provider',
          name: RouteName.AuthMobileCallback,
          component: () => import('../views/auth/MobileSSO.vue'),
        },
        {
          path: 'login',
          name: RouteName.AuthLogin,
          redirect: { name: RouteName.AuthIndex },
        },
        {
          path: 'register',
          name: RouteName.AuthRegister,
          component: () => import('../views/auth/Register.vue'),
        },
        {
          path: 'verify-email',
          name: RouteName.AuthVerifyEmail,
          component: () => import('../views/auth/VerifyEmail.vue'),
        },
        {
          path: 'forgot-password',
          name: RouteName.AuthForgotPassword,
          component: () => import('../views/auth/SendResetPassword.vue'),
        },
        {
          path: 'reset-password',
          name: RouteName.AuthResetPassword,
          component: () => import('../views/auth/ResetPassword.vue'),
        },
      ],
    },
    // Without this, a path no route matches renders an empty RouterView — a blank
    // screen the user cannot navigate out of. A stale or malformed deep link lands
    // on the overview instead.
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: RouteName.Overview },
    },
  ],
})

router.beforeEach(() => {
  const uiStore = useUiStore()
  if (uiStore.dialogStack.length > 0) {
    return false
  }
})

router.onError((error, to) => {
  if (
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed')) &&
    !window.location.search.includes('reloaded=1')
  ) {
    const separator = to.fullPath.includes('?') ? '&' : '?'
    window.location.assign(to.fullPath + separator + 'reloaded=1')
  }
})

export default router
